import React, { FC, useEffect, useState, ChangeEvent, FocusEvent } from 'react';
import { UiSelect, TOption, UiCheckbox, UiInput } from '@frontend/znanie-uikit';

import { useLazyUniversities } from 'hooks/universities/useUniversities';

import * as C from './UniversitiesSelector.const';
import * as T from './UniversitiesSelector.types';

import { UniversitiesRealType } from 'graphql/types';

import css from './UniversitiesSelector.module.scss';

export const UniversitiesSelector: FC<T.IUniversitiesSelectorProps> = ({
  id,
  tabIndex,
  defaultOption,
  errorLabel,
  onSelect,
  onBlur,
  customLabel,
  customPlaceholder,
  customNoOptionsLabel,
  disabled,
  onChangeCustomUniversity,
  defaultCustomUniversity,
  onBlurCustomUniversity,
  onChangeCustomUniversityCheckbox,
  errorLabelCustomUniversity,
  viewCustomUniversity,
  valueCustomUniversity,
  setFieldValue,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [_, setUniversityDetail] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<TOption>();
  let loadTimeoutId: NodeJS.Timeout;

  const [getUniversities] = useLazyUniversities({
    searchQuery: inputValue,
  });

  const onSelectUniversity = (option: any): void => {
    if (option) {
      setSelectedOption(option);
      if (onSelect) onSelect(option);
    }
  };

  const loadOptions = async (
    inputValue: string,
    callback: Function
  ): Promise<void> => {
    try {
      if (inputValue) {
        const result = await getUniversities({
          variables: { searchQuery: inputValue },
        });
        const universitiesOptions = (
          result.data?.searchUniversities as UniversitiesRealType[]
        )?.map((item) => ({
          label: item.name,
          value: item.name,
        })) as TOption[];
        callback(universitiesOptions);
      } else callback([]);
    } catch (_) {
      callback([]);
    }
  };

  const onLoadUniversities = (
    inputValue: string,
    callback: (options: TOption[]) => void
  ): void => {
    if (loadTimeoutId) {
      clearTimeout(loadTimeoutId);
    }
    loadTimeoutId = setTimeout(async () => {
      await loadOptions(inputValue || defaultOption?.value || '', callback);
    }, C.LOAD_TIMEOUT);
  };

  const onChangeCheckbox = ({
    target: { checked },
  }: ChangeEvent<HTMLInputElement>): void => {
    setIsChecked(checked);
    setFieldValue && setFieldValue('university', '');
    onChangeCustomUniversityCheckbox &&
      onChangeCustomUniversityCheckbox(checked);
  };

  const changeCustomUniversity = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>): void => {
    setUniversityDetail(value);
    onChangeCustomUniversity && onChangeCustomUniversity(value);
  };

  const blurCustomUniversity = ({
    target: { value },
  }: FocusEvent<HTMLInputElement>): void => {
    setUniversityDetail(value);
    onBlurCustomUniversity && onBlurCustomUniversity(value);
  };

  useEffect(() => {
    if (defaultOption) {
      setSelectedOption(defaultOption);
    }
  }, [defaultOption]);

  useEffect(() => {
    if (viewCustomUniversity && defaultCustomUniversity) {
      setIsChecked(true);
      setUniversityDetail(defaultCustomUniversity);
    }
  }, [defaultCustomUniversity]);

  useEffect(() => {
    if (Object.keys(defaultOption ?? {}).length) {
      setIsDisabled(true);
    }
  }, [selectedOption, defaultOption]);

  return (
    <>
      <UiSelect
        async
        id={id}
        instanceId={id}
        name={id}
        tabIndex={Number(tabIndex)}
        label={customLabel || 'Укажите ВУЗ'}
        placeholder={customPlaceholder || 'Выберите ВУЗ'}
        isSearchable
        cacheOptions
        defaultOptions={!!defaultOption}
        loadOptions={onLoadUniversities}
        value={selectedOption}
        noOptionsMessage={() =>
          customNoOptionsLabel || 'Начните вводить название ВУЗа'
        }
        loadingMessage={() => 'Идет поиск...'}
        onChange={onSelectUniversity}
        onBlur={onBlur}
        onInputChange={(value) => setInputValue(value)}
        errorLabel={errorLabel}
        isDisabled={disabled || isChecked}
      />
      {viewCustomUniversity && (
        <div className={css.checkBox}>
          <UiCheckbox
            checked={isChecked}
            onChange={onChangeCheckbox}
            label="В списке нет нужного ВУЗа"
            type="checkbox"
            disabled={isDisabled}
          />
          {isChecked && (
            <UiInput
              defaultValue={defaultCustomUniversity}
              fluid
              value={valueCustomUniversity}
              maxLength={150}
              placeholder="Введите название университета"
              onChange={changeCustomUniversity}
              onBlur={blurCustomUniversity}
              errorLabel={errorLabelCustomUniversity}
            />
          )}
        </div>
      )}
    </>
  );
};
